import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ApiError,
  api,
  type CategoryNode,
  type Marketplace,
  type Run,
  type RunCreateBody,
  type RunPreflight,
  type RunSchedule,
  type RunScheduleBody,
  type RunScheduleFrequency,
  type RunScheduleMonthPolicy,
  type RunSchedulesPage,
} from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { InfoHint } from '../components/InfoHint';
import { ToggleSwitch } from '../components/ToggleSwitch';
import {
  categorySelectionStatus,
  removeCategorySelection,
  toggleCategoryPickerState,
} from '../lib/categorySelection';
import {
  clampMonthInputValue,
  currentMonthInputValue,
  fromApiMonthValue,
  openNativeDatePicker,
  previousMonthInputValue,
  toApiMonthValue,
} from '../lib/runSchedule';

// Пороги по умолчанию — совпадают с backend (mp_trend.analytics.rules).
const DEFAULT_THRESHOLDS = {
  new_winner_min_revenue: 1_000_000,
  growth_rate: 1.0,
  min_prev_revenue: 100_000,
};

const MARKETPLACES: Marketplace[] = ['WB']; // MVP — только Wildberries.
const FREQUENCY_LABELS: Record<RunScheduleFrequency, string> = {
  MONTHLY: 'Ежемесячно',
  WEEKLY: 'Еженедельно',
  BIWEEKLY: 'Раз в две недели',
};
const STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Активно',
  PAUSED: 'Пауза',
  CANCELLED: 'Отменено',
  DELETED: 'Удалено',
};

type ToastState = {
  kind: 'success' | 'warning' | 'error';
  title: string;
  message: string;
};

type DuplicateMatch = {
  id: string;
  title: string;
  status: string;
  next_run_at?: string;
};

type DuplicatePrompt = {
  body: RunScheduleBody;
  matches: DuplicateMatch[];
};

function defaultMonthIso(): string {
  return previousMonthInputValue();
}

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

export function RunFormPage() {
  const { user } = useAuth();
  const canCreateRun = user?.role === 'admin' || user?.role === 'user';

  // Доступ: admin и user; viewer смотрит уже созданные прогоны без запуска.
  if (user && !canCreateRun) return <Navigate to="/" replace />;

  return <RunForm />;
}

function RunForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const scheduleId = searchParams.get('scheduleId');
  const maxMonth = currentMonthInputValue();

  // Основные поля формы
  const [title, setTitle] = useState('');
  const [marketplace, setMarketplace] = useState<Marketplace>('WB');
  const [month, setMonth] = useState(defaultMonthIso());
  const [selected, setSelected] = useState<CategoryNode[]>([]);

  const [thresholds, setThresholds] = useState(DEFAULT_THRESHOLDS);
  const [llmEnabled, setLlmEnabled] = useState(true);
  const [bhapiTopN, setBhapiTopN] = useState(20);
  const [advanced, setAdvanced] = useState(false);
  const [submitMode, setSubmitMode] = useState<'schedule' | 'now'>('schedule');
  const [frequency, setFrequency] = useState<RunScheduleFrequency>('MONTHLY');
  const [monthPolicy, setMonthPolicy] =
    useState<RunScheduleMonthPolicy>('PREVIOUS_COMPLETED');
  const [fixedMonth, setFixedMonth] = useState(defaultMonthIso());
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [dayOfWeek, setDayOfWeek] = useState(0);
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [scheduleHour, setScheduleHour] = useState(2);
  const [scheduleMinute, setScheduleMinute] = useState(0);
  const [scheduleTimezone, setScheduleTimezone] = useState('Europe/Moscow');
  const [schedules, setSchedules] = useState<RunSchedule[]>([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [scheduleMessage, setScheduleMessage] = useState<string | null>(null);
  const [editingScheduleId, setEditingScheduleId] = useState<string | null>(null);
  const [lastSavedScheduleSignature, setLastSavedScheduleSignature] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [duplicatePrompt, setDuplicatePrompt] = useState<DuplicatePrompt | null>(null);

  // Typeahead
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounced(query, 250);
  const [suggestions, setSuggestions] = useState<CategoryNode[]>([]);
  const [loadingSug, setLoadingSug] = useState(false);
  const [sugError, setSugError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const toastTimerRef = useRef<number | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingSug(true);
    setSugError(null);
    const qs = debouncedQuery ? `?q=${encodeURIComponent(debouncedQuery)}&limit=20` : '?limit=20';
    api
      .get<CategoryNode[]>(`/api/categories${qs}`)
      .then((data) => {
        if (!cancelled) setSuggestions(data);
      })
      .catch((err) => {
        if (!cancelled) setSugError(err instanceof ApiError ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoadingSug(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  // Закрытие дропдауна при клике вне.
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current !== null) {
        window.clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  function showToast(nextToast: ToastState) {
    if (toastTimerRef.current !== null) {
      window.clearTimeout(toastTimerRef.current);
    }
    setToast(nextToast);
    toastTimerRef.current = window.setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, 6500);
  }

  async function loadSchedules() {
    setScheduleLoading(true);
    setScheduleError(null);
    try {
      const data = await api.get<RunSchedulesPage>(
        '/api/run-schedules?limit=200&sort_by=next_run_at&sort_dir=asc',
      );
      setSchedules(data.items);
    } catch (err) {
      setScheduleError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setScheduleLoading(false);
    }
  }

  useEffect(() => {
    void loadSchedules();
  }, []);

  function toggleCategory(node: CategoryNode) {
    const next = toggleCategoryPickerState({ selected, query }, node);
    setSelected(next.selected);
    setQuery(next.query);
    setDropdownOpen(next.dropdownOpen);
  }

  function removeCategory(path: string) {
    setSelected((prev) => removeCategorySelection(prev, path));
  }

  function clearCategories() {
    setSelected([]);
  }

  // Submit
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [preflight, setPreflight] = useState<RunPreflight | null>(null);
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [preflightError, setPreflightError] = useState<string | null>(null);

  function buildBody(): RunCreateBody {
    return {
      title: title.trim() || null,
      month: toApiMonthValue(month),
      marketplace,
      categories_scope: selected.map((c) => c.path),
      llm_enabled: llmEnabled,
      bhapi_top_n: bhapiTopN,
      thresholds,
    };
  }

  function buildScheduleBody(): RunScheduleBody {
    return {
      title: title.trim() || null,
      marketplace,
      frequency,
      month_policy: monthPolicy,
      fixed_month: monthPolicy === 'FIXED' ? toApiMonthValue(fixedMonth) : null,
      day_of_month: frequency === 'MONTHLY' ? dayOfMonth : null,
      day_of_week: frequency === 'WEEKLY' ? dayOfWeek : null,
      start_date: frequency === 'BIWEEKLY' ? startDate : null,
      hour: scheduleHour,
      minute: scheduleMinute,
      timezone: scheduleTimezone,
      categories_scope: selected.map((c) => c.path),
      llm_enabled: llmEnabled,
      bhapi_top_n: bhapiTopN,
      thresholds,
    };
  }

  function scheduleToBody(schedule: RunSchedule): RunScheduleBody {
    return {
      title: schedule.title || null,
      marketplace: schedule.marketplace,
      frequency: schedule.frequency,
      month_policy: schedule.month_policy,
      fixed_month: schedule.fixed_month,
      day_of_month: schedule.day_of_month,
      day_of_week: schedule.day_of_week,
      start_date: schedule.start_date,
      hour: schedule.hour,
      minute: schedule.minute,
      timezone: schedule.timezone,
      categories_scope: [...schedule.categories_scope],
      thresholds: schedule.thresholds
        ? {
            new_winner_min_revenue:
              Number(schedule.thresholds.new_winner_min_revenue) ||
              DEFAULT_THRESHOLDS.new_winner_min_revenue,
            growth_rate:
              Number(schedule.thresholds.growth_rate) || DEFAULT_THRESHOLDS.growth_rate,
            min_prev_revenue:
              Number(schedule.thresholds.min_prev_revenue) ||
              DEFAULT_THRESHOLDS.min_prev_revenue,
          }
        : DEFAULT_THRESHOLDS,
      llm_enabled: schedule.llm_enabled,
      bhapi_top_n: schedule.bhapi_top_n ?? 20,
    };
  }

  function scheduleSignatureFromBody(body: RunScheduleBody): string {
    return JSON.stringify({
      ...body,
      categories_scope: [...body.categories_scope].sort(),
      thresholds: {
        new_winner_min_revenue: Number(body.thresholds?.new_winner_min_revenue ?? 0),
        growth_rate: Number(body.thresholds?.growth_rate ?? 0),
        min_prev_revenue: Number(body.thresholds?.min_prev_revenue ?? 0),
      },
    });
  }

  function editSchedule(schedule: RunSchedule) {
    const scheduleBody = scheduleToBody(schedule);
    setEditingScheduleId(schedule.id);
    setSubmitMode('schedule');
    setTitle(schedule.title);
    setMarketplace(schedule.marketplace);
    setFrequency(schedule.frequency);
    setMonthPolicy(schedule.month_policy);
    setFixedMonth(clampMonthInputValue(fromApiMonthValue(schedule.fixed_month) || defaultMonthIso()));
    setDayOfMonth(schedule.day_of_month ?? 1);
    setDayOfWeek(schedule.day_of_week ?? 0);
    setStartDate(schedule.start_date ?? new Date().toISOString().slice(0, 10));
    setScheduleHour(schedule.hour);
    setScheduleMinute(schedule.minute);
    setScheduleTimezone(schedule.timezone);
    setSelected(
      schedule.categories_scope.map((path) => ({
        path,
        name: path.split('/').at(-1) || path,
      })),
    );
    if (schedule.thresholds) {
      setThresholds({
        new_winner_min_revenue:
          Number(schedule.thresholds.new_winner_min_revenue) ||
          DEFAULT_THRESHOLDS.new_winner_min_revenue,
        growth_rate:
          Number(schedule.thresholds.growth_rate) || DEFAULT_THRESHOLDS.growth_rate,
        min_prev_revenue:
          Number(schedule.thresholds.min_prev_revenue) ||
          DEFAULT_THRESHOLDS.min_prev_revenue,
      });
    } else {
      setThresholds(DEFAULT_THRESHOLDS);
    }
    setLlmEnabled(schedule.llm_enabled);
    setBhapiTopN(schedule.bhapi_top_n ?? 20);
    setScheduleMessage(null);
    setDuplicatePrompt(null);
    setLastSavedScheduleSignature(scheduleSignatureFromBody(scheduleBody));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  useEffect(() => {
    if (!scheduleId) return;
    let cancelled = false;
    setScheduleLoading(true);
    setScheduleError(null);
    api
      .get<RunSchedule>(`/api/run-schedules/${scheduleId}`)
      .then((schedule) => {
        if (!cancelled) editSchedule(schedule);
      })
      .catch((err) => {
        if (!cancelled) setScheduleError(err instanceof ApiError ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setScheduleLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scheduleId]);

  async function changeScheduleStatus(
    schedule: RunSchedule,
    action: 'pause' | 'resume' | 'cancel',
  ) {
    const labels = {
      pause: 'приостановить',
      resume: 'возобновить',
      cancel: 'отменить',
    };
    if (action === 'cancel' && !window.confirm('Отменить расписание?')) return;
    setScheduleError(null);
    try {
      await api.post<RunSchedule>(`/api/run-schedules/${schedule.id}/${action}`);
      setScheduleMessage(`Расписание ${labels[action]}.`);
      showToast({
        kind: 'success',
        title: 'Готово',
        message: `Расписание ${labels[action]}.`,
      });
      await loadSchedules();
    } catch (err) {
      setScheduleError(err instanceof ApiError ? err.message : String(err));
    }
  }

  async function runScheduleNow(schedule: RunSchedule) {
    setScheduleError(null);
    try {
      const run = await api.post<Run>(`/api/run-schedules/${schedule.id}/run-now`);
      showToast({
        kind: 'success',
        title: 'Прогон создан',
        message: 'Ручной запуск по расписанию поставлен в очередь.',
      });
      navigate(`/runs/${run.id}`);
    } catch (err) {
      setScheduleError(err instanceof ApiError ? err.message : String(err));
    }
  }

  async function deleteSchedule(schedule: RunSchedule) {
    const ok = window.confirm(
      'Удалить расписание? Если по нему уже были прогоны, запись будет скрыта, а история останется доступной администратору для восстановления.',
    );
    if (!ok) return;
    setScheduleError(null);
    try {
      await api.delete(`/api/run-schedules/${schedule.id}`);
      showToast({
        kind: 'success',
        title: 'Расписание удалено',
        message: 'Оно больше не показывается в списках.',
      });
      if (editingScheduleId === schedule.id) {
        setEditingScheduleId(null);
        setLastSavedScheduleSignature(null);
      }
      await loadSchedules();
    } catch (err) {
      setScheduleError(err instanceof ApiError ? err.message : String(err));
    }
  }

  async function runPreflight() {
    setPreflightError(null);
    if (selected.length === 0) {
      setPreflightError('Выберите хотя бы одну категорию перед проверкой.');
      return;
    }
    setPreflightLoading(true);
    try {
      const result = await api.post<RunPreflight>('/api/runs/preflight', buildBody());
      setPreflight(result);
    } catch (err) {
      setPreflightError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setPreflightLoading(false);
    }
  }

  function duplicateMatchesFromError(err: ApiError): DuplicateMatch[] | null {
    const detail = err.detail as { detail?: { code?: string; matches?: DuplicateMatch[] } };
    if (detail?.detail?.code !== 'duplicate_run_schedule') return null;
    return Array.isArray(detail.detail.matches) ? detail.detail.matches : [];
  }

  async function saveSchedule(scheduleBody: RunScheduleBody, allowDuplicate = false) {
    const qs = allowDuplicate ? '?allow_duplicate=true' : '';
    try {
      let saved: RunSchedule;
      if (editingScheduleId) {
        saved = await api.put<RunSchedule>(
          `/api/run-schedules/${editingScheduleId}${qs}`,
          scheduleBody,
        );
        setScheduleMessage('Расписание обновлено.');
        showToast({
          kind: 'success',
          title: 'Изменения сохранены',
          message: 'Расписание обновлено и будет использовать новые настройки.',
        });
      } else {
        saved = await api.post<RunSchedule>(`/api/run-schedules${qs}`, scheduleBody);
        setEditingScheduleId(saved.id);
        setScheduleMessage('Расписание сохранено.');
        showToast({
          kind: 'success',
          title: 'Расписание сохранено',
          message: 'Повторное сохранение заблокировано, пока вы не измените настройки.',
        });
      }
      setDuplicatePrompt(null);
      setLastSavedScheduleSignature(scheduleSignatureFromBody(scheduleBody));
      await loadSchedules();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const matches = duplicateMatchesFromError(err);
        if (matches) {
          setDuplicatePrompt({ body: scheduleBody, matches });
          showToast({
            kind: 'warning',
            title: 'Найден похожий прогон',
            message: 'Проверьте совпадение и подтвердите сохранение, если дубль нужен.',
          });
          return;
        }
      }
      throw err;
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting || (submitMode === 'schedule' && scheduleUnchanged)) return;
    setSubmitError(null);
    if (selected.length === 0) {
      setSubmitError('Выберите хотя бы одну категорию перед запуском.');
      return;
    }
    setSubmitting(true);
    const body = buildBody();
    if (submitMode === 'schedule') {
      try {
        const scheduleBody = buildScheduleBody();
        await saveSchedule(scheduleBody);
      } catch (err) {
        setSubmitError(err instanceof ApiError ? err.message : String(err));
      } finally {
        setSubmitting(false);
      }
      return;
    }
    if (
      preflight?.warnings.length
      && !window.confirm('Preflight содержит предупреждения. Всё равно запустить прогон?')
    ) {
      setSubmitting(false);
      return;
    }
    try {
      const run = await api.post<Run>('/api/runs', body);
      navigate(`/runs/${run.id}`, { replace: true });
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  const currentScheduleSignature = scheduleSignatureFromBody(buildScheduleBody());
  const scheduleUnchanged =
    submitMode === 'schedule' &&
    lastSavedScheduleSignature !== null &&
    currentScheduleSignature === lastSavedScheduleSignature;

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-6 max-w-[900px]">
      {toast ? (
        <div className="fixed right-5 top-5 z-50 max-w-[360px] rounded-md border border-border-default/[0.14] bg-panel p-4 shadow-card">
          <div className={toast.kind === 'warning' ? 'pill-warn self-start' : toast.kind === 'error' ? 'pill-danger self-start' : 'pill-success self-start'}>
            {toast.title}
          </div>
          <div className="mt-2 text-label text-text-primary">{toast.message}</div>
        </div>
      ) : null}
      {duplicatePrompt ? (
        <div className="fixed right-5 bottom-5 z-50 max-w-[420px] rounded-md border border-warning/40 bg-panel p-4 shadow-card">
          <div className="pill-warn self-start">Похожее расписание уже есть</div>
          <div className="mt-2 text-label text-text-primary">
            Совпали маркетплейс, частота, месяц отчёта, время, категории и ограничения.
          </div>
          <div className="mt-3 grid gap-2">
            {duplicatePrompt.matches.slice(0, 3).map((match) => (
              <div key={match.id} className="rounded-sm border border-border-default/[0.1] bg-surface-2 px-3 py-2">
                <div className="text-label text-text-primary">{match.title}</div>
                <div className="mono text-caption text-text-tertiary">{match.id.slice(0, 8)} · {match.status}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-primary btn-sm"
              disabled={submitting}
              onClick={() => {
                setSubmitting(true);
                saveSchedule(duplicatePrompt.body, true)
                  .catch((err) => setSubmitError(err instanceof ApiError ? err.message : String(err)))
                  .finally(() => setSubmitting(false));
              }}
            >
              Всё равно сохранить
            </button>
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={() => setDuplicatePrompt(null)}
            >
              Вернуться к настройкам
            </button>
          </div>
        </div>
      ) : null}
      <div className="section-header page-header">
        <div>
          <div className="rubric">Новая задача</div>
          <h1 className="text-h1 font-strong text-text-primary">Запуск прогона</h1>
        </div>
      </div>

      {/* Блок 1: название + период */}
      <div className="card card-padding flex flex-col gap-4">
        <div>
          <label className="label inline-flex items-center gap-1" htmlFor="title">
            Краткое название
            <InfoHint text="Название помогает отличать несколько прогонов за один месяц. Если оставить поле пустым, система подставит его автоматически." />
          </label>
          <input
            id="title"
            type="text"
            maxLength={160}
            className="input mt-1"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Например: Проверка платьев, май"
          />
          <div className="hint mt-1">
            Помогает отличать несколько прогонов за один месяц. Если оставить пустым,
            система задаст название автоматически.
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="label inline-flex items-center gap-1" htmlFor="month">
              Месяц анализа
              <InfoHint text="Один прогон анализирует выбранный месяц и сравнивает его с предыдущим." />
            </label>
            <input
              id="month"
              type="month"
              className="input mt-1"
              max={maxMonth}
              value={month}
              onClick={(e) => openNativeDatePicker(e.currentTarget)}
              onChange={(e) => setMonth(clampMonthInputValue(e.target.value))}
              required
            />
            <div className="hint mt-1">
              Анализ сравнивает выбранный месяц с предыдущим. Выберите месяц и год.
            </div>
          </div>
          <div>
            <label className="label" htmlFor="marketplace">Маркетплейс</label>
            <select
              id="marketplace"
              className="input mt-1"
              value={marketplace}
              onChange={(e) => setMarketplace(e.target.value as Marketplace)}
            >
              {MARKETPLACES.map((mp) => (
                <option key={mp} value={mp}>{mp}</option>
              ))}
            </select>
            <div className="hint mt-1">Пока подключён только Wildberries.</div>
          </div>
        </div>
      </div>

      {/* Блок 2: категории (multi-select) */}
      <div className="card card-padding flex flex-col gap-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="rubric">Обязательно</div>
            <h2 className="inline-flex items-center gap-2 text-h3 font-strong text-text-primary">
              Категории для анализа
              <InfoHint text="Категории выбираются вручную для каждого прогона или расписания. Запуск по всем категориям не используется, чтобы беречь квоты MPSTATS." />
            </h2>
          </div>
          <div className={selected.length > 0 ? 'pill-success' : 'pill-neutral'}>
            {selected.length > 0 ? '✓ ' : ''}
            Выбрано: {selected.length}
          </div>
        </div>
        <div className="hint">
          Выберите категории вручную. Дефолтного списка нет — каждый прогон настраивается под задачу.
        </div>

        {selected.length > 0 ? (
          <div className="rounded-md border border-accent/40 bg-accent/10 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-label font-medium text-text-primary">
                Выбранные категории
              </div>
              <button
                type="button"
                className="btn-ghost btn-sm"
                onClick={clearCategories}
              >
                Очистить все
              </button>
            </div>
            <div className="mt-3 grid gap-2">
              {selected.map((s) => (
                <div
                  key={s.path}
                  className="flex min-w-0 items-start gap-2 rounded-sm border border-accent/25 bg-surface/80 px-2.5 py-2"
                >
                  <span
                    className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-sm bg-accent text-caption font-medium text-white"
                    aria-hidden="true"
                  >
                    ✓
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-label font-medium text-text-primary break-words">{s.name}</div>
                    <div className="mono text-caption text-text-secondary break-words">{s.path}</div>
                  </div>
                  <button
                    type="button"
                    className="btn-ghost btn-sm shrink-0 text-text-secondary hover:text-danger"
                    aria-label={`Снять выбор категории ${s.path}`}
                    onClick={() => removeCategory(s.path)}
                  >
                    Убрать
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="rounded-md border border-dashed border-border-default/[0.18] bg-surface-2/50 px-3 py-3 text-caption text-text-tertiary">
            Категории ещё не выбраны.
          </div>
        )}

        <div className="relative" ref={dropdownRef}>
          <input
            className="input"
            placeholder="Начните печатать путь категории, например «Женщинам/Одежда»"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setDropdownOpen(true);
            }}
            onFocus={() => setDropdownOpen(true)}
          />

          {dropdownOpen ? (
            <div className="absolute z-20 mt-1 w-full max-h-[320px] overflow-auto bg-panel border border-border-default/[0.08] rounded-md shadow-md">
              {loadingSug ? (
                <div className="px-3 py-2 text-text-tertiary text-caption">Загрузка…</div>
              ) : sugError ? (
                <div className="px-3 py-2 text-danger text-caption">{sugError}</div>
              ) : suggestions.length === 0 ? (
                <div className="px-3 py-2 text-text-tertiary text-caption">Ничего не найдено</div>
              ) : (
                suggestions.map((s) => {
                  const status = categorySelectionStatus(selected, s.path);
                  const taken = status === 'selected';
                  return (
                    <button
                      key={s.path}
                      type="button"
                      className={[
                        'group flex w-full items-start gap-2 text-left px-3 py-2 text-label transition-colors',
                        taken
                          ? 'bg-accent/10 text-text-primary hover:bg-accent/15'
                          : 'text-text-primary hover:bg-surface-2',
                      ].join(' ')}
                      onClick={() => {
                        toggleCategory(s);
                      }}
                    >
                      <span
                        className={[
                          'mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border text-caption',
                          taken
                            ? 'border-accent bg-accent text-white'
                            : 'border-border-default/[0.12] text-text-quaternary group-hover:border-accent/60',
                        ].join(' ')}
                        aria-hidden="true"
                      >
                        {taken ? '✓' : '+'}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium">{s.name}</span>
                        <span className="mono block text-caption text-text-tertiary truncate">{s.path}</span>
                      </span>
                      {taken ? (
                        <span className="pill-success shrink-0">выбрано</span>
                      ) : null}
                    </button>
                  );
                })
              )}
            </div>
          ) : null}
        </div>
      </div>

      {/* Блок 3: preflight */}
      <div className="card card-padding flex flex-col gap-4">
          <div>
            <div className="rubric">Режим</div>
          <h2 className="inline-flex items-center gap-2 text-h3 font-strong text-text-primary">
            Запуск и расписание
            <InfoHint text="Можно сразу запустить разовый прогон или сохранить расписание, которое будет создавать прогоны автоматически." />
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <label className="flex items-center gap-2 rounded-md border border-border-default/[0.12] px-3 py-2">
            <input type="radio" checked={submitMode === 'schedule'} onChange={() => setSubmitMode('schedule')} />
            <span className="text-label text-text-primary">Сохранить расписание</span>
          </label>
          <label className="flex items-center gap-2 rounded-md border border-border-default/[0.12] px-3 py-2">
            <input type="radio" checked={submitMode === 'now'} onChange={() => setSubmitMode('now')} />
            <span className="text-label text-text-primary">Запустить сейчас</span>
          </label>
        </div>
        {submitMode === 'schedule' ? (
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <label className="label" htmlFor="schedule-frequency">Частота</label>
              <select id="schedule-frequency" className="input mt-1" value={frequency} onChange={(e) => setFrequency(e.target.value as RunScheduleFrequency)}>
                <option value="MONTHLY">Ежемесячно</option>
                <option value="WEEKLY">Еженедельно</option>
                <option value="BIWEEKLY">Раз в две недели</option>
              </select>
            </div>
            <div>
              <label className="label" htmlFor="month-policy">Месяц анализа</label>
              <select id="month-policy" className="input mt-1" value={monthPolicy} onChange={(e) => setMonthPolicy(e.target.value as RunScheduleMonthPolicy)}>
                <option value="PREVIOUS_COMPLETED">Предыдущий завершённый</option>
                <option value="CURRENT">Текущий</option>
                <option value="FIXED">Фиксированный</option>
              </select>
            </div>
            {monthPolicy === 'FIXED' ? (
              <div>
                <label className="label" htmlFor="fixed-month">Фиксированный месяц</label>
                <input
                  id="fixed-month"
                  type="month"
                  className="input mt-1"
                  max={maxMonth}
                  value={fixedMonth}
                  onClick={(e) => openNativeDatePicker(e.currentTarget)}
                  onChange={(e) => setFixedMonth(clampMonthInputValue(e.target.value))}
                />
              </div>
            ) : null}
            {frequency === 'MONTHLY' ? (
              <div>
                <label className="label" htmlFor="schedule-day-month">День месяца</label>
                <input id="schedule-day-month" type="number" min={1} max={31} className="input mt-1" value={dayOfMonth} onChange={(e) => setDayOfMonth(Math.min(31, Math.max(1, Number(e.target.value) || 1)))} />
              </div>
            ) : null}
            {frequency === 'WEEKLY' ? (
              <div>
                <label className="label" htmlFor="schedule-day-week">День недели</label>
                <select id="schedule-day-week" className="input mt-1" value={dayOfWeek} onChange={(e) => setDayOfWeek(Number(e.target.value))}>
                  <option value={0}>Понедельник</option>
                  <option value={1}>Вторник</option>
                  <option value={2}>Среда</option>
                  <option value={3}>Четверг</option>
                  <option value={4}>Пятница</option>
                  <option value={5}>Суббота</option>
                  <option value={6}>Воскресенье</option>
                </select>
              </div>
            ) : null}
            {frequency === 'BIWEEKLY' ? (
              <div>
                <label className="label" htmlFor="schedule-start-date">Дата старта</label>
                <input
                  id="schedule-start-date"
                  type="date"
                  className="input mt-1"
                  value={startDate}
                  onClick={(e) => openNativeDatePicker(e.currentTarget)}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
            ) : null}
            <div>
              <label className="label" htmlFor="schedule-hour">Время</label>
              <div className="mt-1 grid grid-cols-2 gap-2">
                <input id="schedule-hour" type="number" min={0} max={23} className="input" value={scheduleHour} onChange={(e) => setScheduleHour(Math.min(23, Math.max(0, Number(e.target.value) || 0)))} />
                <input type="number" min={0} max={59} className="input" value={scheduleMinute} onChange={(e) => setScheduleMinute(Math.min(59, Math.max(0, Number(e.target.value) || 0)))} />
              </div>
            </div>
            <div>
              <label className="label" htmlFor="schedule-timezone">Часовой пояс</label>
              <input id="schedule-timezone" type="text" className="input mt-1" value={scheduleTimezone} onChange={(e) => setScheduleTimezone(e.target.value)} />
            </div>
          </div>
        ) : null}
      </div>

      <div className="card card-padding flex flex-col gap-3">
          <div>
            <div className="rubric">Preflight</div>
          <h2 className="inline-flex items-center gap-2 text-h3 font-strong text-text-primary">
            Проверка перед запуском
            <InfoHint text="Preflight оценивает число запросов, остаток квоты, риск превышения лимитов и выбранные параметры до создания прогона." />
          </h2>
          <div className="hint mt-1">
            Оценка запросов, остатка квоты и рисков выбранных настроек.
          </div>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <button
            type="button"
            className="btn-secondary btn-md"
            disabled={preflightLoading || selected.length === 0}
            onClick={() => void runPreflight()}
          >
            {preflightLoading ? 'Проверка…' : 'Проверить запуск'}
          </button>
          {preflightError ? <span className="error-hint">{preflightError}</span> : null}
        </div>
        {preflight ? (
          <div className="grid md:grid-cols-3 gap-3">
            <div className="card card-padding bg-surface-2">
              <div className="rubric">Категории</div>
              <div className="text-h3 text-text-primary font-strong">
                {preflight.categories_count}
              </div>
            </div>
            <div className="card card-padding bg-surface-2">
              <div className="rubric">Оценка запросов</div>
              <div className="text-h3 text-text-primary font-strong">
                {preflight.estimated_mpstats_requests}
              </div>
            </div>
            <div className="card card-padding bg-surface-2">
              <div className="rubric">Старт</div>
              <div className={preflight.can_start ? 'pill-success' : 'pill-danger'}>
                {preflight.can_start ? 'разрешён' : 'заблокирован'}
              </div>
            </div>
            {preflight.warnings.length > 0 ? (
              <div className="md:col-span-3 card card-padding border-warning/30">
                <div className="rubric">Предупреждения</div>
                <ul className="list-disc pl-5 text-caption text-text-secondary">
                  {preflight.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="md:col-span-3 text-caption text-text-tertiary">
                Предупреждений нет.
              </div>
            )}
            {preflight.api_limit_snapshot ? (
              <pre className="md:col-span-3 mono text-caption text-text-secondary overflow-auto max-h-48 bg-surface-2 rounded-md p-3">
                {JSON.stringify(preflight.api_limit_snapshot, null, 2)}
              </pre>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Блок 3: AI + детализация */}
      <div className="card card-padding flex flex-col gap-3">
        <h2 className="inline-flex items-center gap-2 text-h3 font-strong text-text-primary">
          Обогащение
          <InfoHint text="AI объясняет уже найденные события, а детализация подтягивает дополнительные данные по TOP-карточкам." />
        </h2>
        <ToggleSwitch
          checked={llmEnabled}
          onChange={setLlmEnabled}
          label="AI-комментарии"
          description="Объясняет уже найденные события и формирует разделы 4–7. На отбор карточек и раздел 3 не влияет."
        />
        <div>
          <div className="inline-flex items-center gap-2">
            <label className="label" htmlFor="detail-top-n">Карточек для детализации</label>
            <InfoHint text="Детализация запускается после отбора событий и не меняет показатели MPSTATS." />
          </div>
          <input
            id="detail-top-n"
            type="number"
            min={0}
            max={500}
            className="input mt-1 max-w-[200px]"
            value={bhapiTopN}
            onChange={(e) => setBhapiTopN(Math.max(0, Number(e.target.value) || 0))}
          />
          <div className="hint mt-1">
            Дополнить данными TOP-N уже отобранных карточек разделов 1–2. Состав отчёта не меняется; 0 — отключить.
          </div>
        </div>
      </div>

      {/* Блок 4: пороги (расширенные настройки) */}
      <div className="card card-padding flex flex-col gap-3">
        <div className="inline-flex items-center gap-2 self-start">
          <button
            type="button"
            className="btn-ghost btn-sm"
            onClick={() => setAdvanced((v) => !v)}
          >
            {advanced ? '▾' : '▸'} Расширенные настройки порогов
          </button>
          <InfoHint text="Эти пороги управляют только разделами 1 и 2. Раздел 3 сравнивает отдельные агрегаты брендов и продавцов за два месяца." />
        </div>
        {advanced ? (
          <div className="space-y-3">
            <div className="rounded-md bg-surface-2 p-3 text-caption text-text-secondary">
              Пороги ниже влияют только на карточки разделов 1–2. Раздел 3 формируется отдельным сравнением агрегатов брендов и продавцов выбранного и предыдущего месяца.
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <label className="label" htmlFor="new-rev">Раздел 1 · минимум заказов, ₽</label>
                <input
                  id="new-rev"
                  type="number"
                  min={0}
                  className="input mt-1"
                  value={thresholds.new_winner_min_revenue}
                  onChange={(e) =>
                    setThresholds({ ...thresholds, new_winner_min_revenue: Number(e.target.value) || 0 })
                  }
                />
                <div className="hint mt-1">Новая карточка попадёт в раздел 1, если сумма заказов MPSTATS за месяц не ниже этого значения.</div>
              </div>
              <div>
                <label className="label" htmlFor="growth">Раздел 2 · рост к прошлому месяцу</label>
                <input
                  id="growth"
                  type="number"
                  min={0}
                  step={0.1}
                  className="input mt-1"
                  value={thresholds.growth_rate}
                  onChange={(e) =>
                    setThresholds({ ...thresholds, growth_rate: Number(e.target.value) || 0 })
                  }
                />
                <div className="hint mt-1">0.3 = рост более 30%; 1.0 = более 100%. Применяется только в разделе 2.</div>
              </div>
              <div>
                <label className="label" htmlFor="min-prev">Раздел 2 · заказы прошлого месяца от, ₽</label>
                <input
                  id="min-prev"
                  type="number"
                  min={0}
                  className="input mt-1"
                  value={thresholds.min_prev_revenue}
                  onChange={(e) =>
                    setThresholds({ ...thresholds, min_prev_revenue: Number(e.target.value) || 0 })
                  }
                />
                <div className="hint mt-1">Защита раздела 2 от случайно большого роста с низкой базы прошлого месяца.</div>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <div className="card card-padding flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="rubric">Расписания</div>
            <h2 className="inline-flex items-center gap-2 text-h3 font-strong text-text-primary">
              Сохранённые запуски
              <InfoHint text="Сохранённые расписания имеют собственные параметры и не меняются задним числом от дефолтов." />
            </h2>
          </div>
          <button type="button" className="btn-ghost btn-sm" onClick={() => void loadSchedules()}>
            Обновить
          </button>
        </div>
        {scheduleMessage ? <div className="pill-success self-start">{scheduleMessage}</div> : null}
        {scheduleError ? <div className="error-hint">{scheduleError}</div> : null}
        {scheduleLoading ? (
          <div className="text-caption text-text-tertiary">Загрузка…</div>
        ) : schedules.length === 0 ? (
          <div className="text-caption text-text-tertiary">Расписаний пока нет.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-label">
              <thead>
                <tr className="text-left text-text-tertiary">
                  <th className="py-2 pr-3">Название</th>
                  <th className="py-2 pr-3">Частота</th>
                  <th className="py-2 pr-3">Статус</th>
                  <th className="py-2 pr-3">Следующий</th>
                  <th className="py-2 pr-3">Действия</th>
                </tr>
              </thead>
              <tbody>
                {schedules.map((schedule) => (
                  <tr key={schedule.id} className="border-t border-border-default/[0.08]">
                    <td className="py-2 pr-3 text-text-primary">{schedule.title}</td>
                    <td className="py-2 pr-3">{FREQUENCY_LABELS[schedule.frequency]}</td>
                    <td className="py-2 pr-3">{STATUS_LABELS[schedule.status] ?? schedule.status}</td>
                    <td className="py-2 pr-3">
                      {schedule.status === 'CANCELLED'
                        ? '—'
                        : new Date(schedule.next_run_at).toLocaleString()}
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex flex-wrap gap-2">
                        <button type="button" className="btn-ghost btn-sm" onClick={() => editSchedule(schedule)}>
                          Изменить
                        </button>
                        {schedule.status !== 'CANCELLED' ? (
                          <button type="button" className="btn-ghost btn-sm" onClick={() => void runScheduleNow(schedule)}>
                            Запустить
                          </button>
                        ) : null}
                        {schedule.status === 'ACTIVE' ? (
                          <button type="button" className="btn-ghost btn-sm" onClick={() => void changeScheduleStatus(schedule, 'pause')}>
                            Пауза
                          </button>
                        ) : null}
                        {schedule.status === 'PAUSED' ? (
                          <button type="button" className="btn-ghost btn-sm" onClick={() => void changeScheduleStatus(schedule, 'resume')}>
                            Возобновить
                          </button>
                        ) : null}
                        {schedule.status !== 'CANCELLED' ? (
                          <button type="button" className="btn-ghost btn-sm text-danger" onClick={() => void changeScheduleStatus(schedule, 'cancel')}>
                            Отменить
                          </button>
                        ) : null}
                        <button type="button" className="btn-ghost btn-sm text-danger" onClick={() => void deleteSchedule(schedule)}>
                          Удалить
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {submitError ? <div className="error-hint">{submitError}</div> : null}

      <div className="flex items-center gap-3">
        <button
          type="submit"
          className="btn-primary btn-md"
          disabled={submitting || selected.length === 0 || scheduleUnchanged}
        >
          {scheduleUnchanged
            ? 'Сохранено'
            : submitting
            ? 'Сохранение…'
            : submitMode === 'now'
              ? 'Запустить прогон'
              : editingScheduleId
                ? 'Сохранить изменения'
                : 'Сохранить расписание'}
        </button>
        <button
          type="button"
          className="btn-ghost btn-md"
          onClick={() => navigate('/', { replace: true })}
          disabled={submitting}
        >
          Отмена
        </button>
      </div>
    </form>
  );
}
